// في PdfExportModal.jsx

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Typography,
    IconButton,
    Stack,
    Switch,
    FormControlLabel,
    CircularProgress,
    Box,
} from "@mui/material";
import { Close, PictureAsPdf } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useState, useMemo, useEffect } from "react";
import { format } from "date-fns";
import ReactDOMServer from "react-dom/server";
import FormInput from "../../inputs/FormInput";
import useGetNationalitiesForSelect from "../../../hooks/nationalities/useGetNationalitiesForSelect";
import useGetCountriesForSelect from "../../../hooks/countries/useGetCountriesForSelect";
import useExportProgramToPdf from "../../../hooks/programs/useExportProgramToPdf";
import useGetProgramPdfSections from "../../../hooks/programs/useGetProgramPdfSections";
import PrintProgram from "./PrintProgram";
import { printProgramStyles } from "../../../utils/programs/printProgramStyles";
import { formatFullPhoneNumber } from "../../../utils/phoneUtils";
import toast from "react-hot-toast";
import { countries } from "../../../constants/countres";

const PdfExportModal = ({ open, onClose, program }) => {
    const { i18n } = useTranslation();
    const lang = i18n.language;

    const { options: nationalityOptionsRaw, isLoading: loadingNationalities } = useGetNationalitiesForSelect();
    const { options: countryOptionsRaw } = useGetCountriesForSelect();
    const { exportPdfMutate, isPending } = useExportProgramToPdf();
    const { pdfSections, isLoading: loadingSections } = useGetProgramPdfSections(program.id);

    // ✅ State منفصل لرقم الهاتف
    const [selectedCountryCode, setSelectedCountryCode] = useState(countries[0].phone);
    const [phoneNumber, setPhoneNumber] = useState("");

    // ✅ Adapter: تحويل البيانات من {value, label} إلى {id, name}
    const nationalityOptions = useMemo(() => {
        return nationalityOptionsRaw?.map(item => ({
            id: item.value,
            name: item.label
        })) || [];
    }, [nationalityOptionsRaw]);

    const countryOptions = useMemo(() => {
        return countryOptionsRaw?.map(item => ({
            id: item.value,
            name: item.label
        })) || [];
    }, [countryOptionsRaw]);

    const [formData, setFormData] = useState({
        agent_name: "",
        agent_country: "",
        req_date: format(new Date(), "yyyy-MM-dd"),
        sending_date: format(new Date(), "yyyy-MM-dd"),
        client_name: "",
        cities: "",
        client_nationality: "",
        include_prices: true,
    });
    const disabledSx = {
        '& .MuiOutlinedInput-root.Mui-disabled': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            color: 'rgba(0, 0, 0, 0.6)',
        },
        '& .MuiInputBase-input.Mui-disabled': {
            WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
        },
        // For native inputs used in FormInput
        '&.input:disabled': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            color: 'rgba(0, 0, 0, 0.6)',
            opacity: 0.8,
        }
    };

    useEffect(() => {
        if (open && program?.description?.[lang]) {
            // Build cities string from array
            const citiesString = (() => {
                if (!program?.cities || !Array.isArray(program.cities)) return '';
                const names = program.cities
                    .sort((a, b) => a.order - b.order)
                    .map(c => `\u2068${c.name?.[lang] || c.name?.en || c.name?.ar || ''}\u2069`);

                if (names.length === 0) return '';
                if (names.length === 1) return names[0];

                const separator = lang === 'ar' ? '، ' : ', ';
                const lastSeparator = lang === 'ar' ? ' و' : ' & ';

                return names.slice(0, -1).join(separator) + lastSeparator + names[names.length - 1];
            })();

            setFormData(prev => ({
                ...prev,
                agent_name: program.agent?.name?.[lang] || program.agent?.name?.en || program.agent_name?.[lang] || program.agent_name || "",
                agent_country: program.agent?.country_id || program.agent?.country?.id || "",
                client_name: program.description?.[lang],
                cities: citiesString
            }));
        }
    }, [open, program?.description, program?.agent, program?.agent_name, program.cities, lang]);
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSwitchChange = (e) => {
        setFormData((prev) => ({
            ...prev,
            include_prices: e.target.checked,
        }));
    };

    // ✅ دالة للحصول على اسم الجنسية - تعمل مع {id, name}
    const getNationalityName = (nationalityId) => {
        if (!nationalityId) return "";
        const nationality = nationalityOptions?.find(nat => nat.id === parseInt(nationalityId));
        if (typeof nationality?.name === 'string') {
            return nationality.name;
        }
        return nationality?.name?.[lang] || nationality?.name?.en || "";
    };

    // ✅ دالة للحصول على اسم الدولة - تعمل مع {id, name}
    const getCountryName = (countryId) => {
        if (!countryId) return "";
        const country = countryOptions?.find(c => c.id === parseInt(countryId));
        if (typeof country?.name === 'string') {
            return country.name;
        }
        return country?.name?.[lang] || country?.name?.en || "";
    };

    const generateHTML = (fullPhone) => {
        const clientInfo = {
            agent_name: formData.agent_name,
            agent_country: getCountryName(formData.agent_country),
            req_date: formData.req_date,
            sending_date: formData.sending_date,
            client_name: formData.client_name,
            phone_number: fullPhone,
            cities: formData.cities,
            client_nationality: getNationalityName(formData.client_nationality),
        };

        const formattedSections = pdfSections ? Object.keys(pdfSections).reduce((acc, key) => {
            acc[key] = {
                title: pdfSections[key]?.title || '',
                items: pdfSections[key]?.items || [],
                note: pdfSections[key]?.note || '',
            };
            return acc;
        }, {}) : null;

        const componentHTML = ReactDOMServer.renderToString(
            <PrintProgram
                program={program}
                lang={lang}
                clientInfo={clientInfo}
                pdfSections={formattedSections}
                includePrices={formData.include_prices}
            />
        );

        const html = `
<!DOCTYPE html>
<html lang="${lang}" dir="${lang === 'ar' ? 'rtl' : 'ltr'}">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${program.title?.[lang] || 'Program'}</title>
    <style>
        ${printProgramStyles}
    </style>
</head>
<body>
    ${componentHTML}
</body>
</html>
    `;

        return html;
    };

    const handleExport = () => {
        // ✅ التحقق من صحة رقم الهاتف
        if (!phoneNumber) {
            toast.error(lang === 'ar' ? 'الرجاء إدخال رقم الهاتف' : 'Please enter phone number');
            return;
        }

        // Removed strict validation to allow any number as requested by user

        // ✅ تكوين الرقم الكامل
        const fullPhone = formatFullPhoneNumber(selectedCountryCode, phoneNumber);

        const html = generateHTML(fullPhone);
        const filename = `${formData.agent_name || 'program'}_${program.program_number}.pdf`;

        const data = {
            html: html,
            filename: filename,
            landscape: false,
            format: "A4",
            agent_name: formData.agent_name,
            agent_country: getCountryName(formData.agent_country),
            req_date: formData.req_date,
            sending_date: formData.sending_date,
            client_name: formData.client_name,
            phone_number: fullPhone, // ✅ إرسال الرقم الكامل
            cities: formData.cities,
            client_nationality: getNationalityName(formData.client_nationality),
            travel_date: program.start_date,
            return_date: program.end_date,
            include_prices: formData.include_prices,
        };

        exportPdfMutate(
            { id: program.id, data },
            {
                onSuccess: () => {
                    onClose();
                    // ✅ إعادة تعيين جميع الحقول بما فيها رقم الهاتف
                    setFormData({
                        agent_name: "",
                        agent_country: "",
                        req_date: format(new Date(), "yyyy-MM-dd"),
                        sending_date: format(new Date(), "yyyy-MM-dd"),
                        client_name: "",
                        cities: "",
                        client_nationality: "",
                        include_prices: true,
                    });
                    setPhoneNumber("");
                    setSelectedCountryCode(countries[0].phone);
                },
            }
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight="bold">
                        {lang === 'ar' ? "تصدير البرنامج إلى PDF" : "Export Program to PDF"}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={2} sx={{ direction: lang === 'ar' ? 'rtl' : 'ltr' }}>
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={lang === 'ar' ? "اسم الوكالة" : "Agent Name"}
                            type="text"
                            name="agent_name"
                            placeholder={lang === 'ar' ? "أدخل اسم الوكالة" : "Enter agent name"}
                            value={formData.agent_name}
                            onChange={handleChange}
                            required={true}
                            disabled={true}
                            sx={disabledSx}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={lang === 'ar' ? "دولة الوكالة" : "Agent Country"}
                            type="select"
                            name="agent_country"
                            value={formData.agent_country}
                            onChange={handleChange}
                            options={countryOptions}
                            required={true}
                            disabled={true}
                            sx={disabledSx}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={lang === 'ar' ? "تاريخ الطلب" : "Request Date"}
                            type="date"
                            name="req_date"
                            value={formData.req_date}
                            onChange={handleChange}
                            required={true}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={lang === 'ar' ? "تاريخ الإرسال" : "Sending Date"}
                            type="date"
                            name="sending_date"
                            value={formData.sending_date}
                            onChange={handleChange}
                            required={true}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={lang === 'ar' ? "اسم العميل" : "Client Name"}
                            type="text"
                            name="client_name"
                            placeholder={lang === 'ar' ? "أدخل اسم العميل" : "Enter client name"}
                            value={formData.client_name}
                            onChange={handleChange}
                            required={true}
                            disabled={true}
                            sx={disabledSx}
                        />
                    </Grid>

                    {/* ✅ حقل رقم الهاتف مع state منفصل */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={lang === 'ar' ? "رقم الهاتف" : "Phone Number"}
                            placeholder="+201000000000"
                            type="tel"
                            name="phone_number"
                            phoneNumber={phoneNumber}
                            setPhoneNumber={setPhoneNumber}
                            selectedCountryCode={selectedCountryCode}
                            setSelectedCountryCode={setSelectedCountryCode}
                            required={true}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={lang === 'ar' ? "المدن" : "Cities"}
                            type="text"
                            name="cities"
                            placeholder={lang === 'ar' ? "القاهرة, الأقصر, أسوان" : "Cairo, Luxor, Aswan"}
                            value={formData.cities}
                            onChange={handleChange}
                            required={false}
                            disabled={true}
                            sx={disabledSx}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={lang === 'ar' ? "جنسية العميل" : "Client Nationality"}
                            type="select"
                            name="client_nationality"
                            value={formData.client_nationality}
                            onChange={handleChange}
                            options={nationalityOptions}
                            required={false}
                            disabled={loadingNationalities}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={lang === 'ar' ? "تاريخ السفر" : "Travel Date"}
                            type="date"
                            name="travel_date"
                            value={program.start_date}
                            disabled={true}
                            required={false}
                            sx={disabledSx}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={lang === 'ar' ? "تاريخ العودة" : "Return Date"}
                            type="date"
                            name="return_date"
                            value={program.end_date}
                            disabled={true}
                            required={false}
                            sx={disabledSx}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ width: '100%', textAlign: lang === 'ar' ? 'right' : 'left' }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.include_prices}
                                        onChange={handleSwitchChange}
                                        color="primary"
                                    />
                                }
                                label={lang === 'ar' ? "تضمين الأسعار في PDF" : "Include Prices in PDF"}
                                labelPlacement={lang === 'ar' ? 'start' : 'end'}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Box sx={{ width: '100%', textAlign: lang === 'ar' ? 'left' : 'right' }}>
                    <Button onClick={onClose} disabled={isPending}>
                        {lang === 'ar' ? "إلغاء" : "Cancel"}
                    </Button>

                    <Button
                        variant="contained"
                        color="error"
                        startIcon={isPending ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdf />}
                        onClick={handleExport}
                        disabled={isPending || !formData.agent_name || !formData.client_name || !phoneNumber || loadingSections}
                    >
                        {isPending
                            ? (lang === 'ar' ? "جاري التصدير..." : "Exporting...")
                            : (lang === 'ar' ? "تصدير PDF" : "Export PDF")}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default PdfExportModal;
